import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Calendar } from '@/shared/components/ui/Calendar';
import { cn } from '@/core/lib/utils';
import { resolveColorValue } from '@/core/lib/color-palette';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';
import type { ProjectFieldOption } from '../types';

interface SaveFn {
  /**
   * Returning `false` signals a client-side validation failure — the editor stays open
   * and shows the `error` prop instead of closing. Any other return value closes it.
   */
  (value: string): boolean | void;
}

interface InlineTextEditorProps {
  value: string | null;
  type?: 'text' | 'number' | 'email' | 'url' | 'tel';
  isPending: boolean;
  onSave: SaveFn;
  /** Client-side format error (from `validateTaskFieldValue`) shown below the control. */
  error?: string | null;
  /** Overrides the default trigger testid — lets per-type editors report their own. */
  testId?: string;
}

export function InlineTextEditor({
  value,
  type = 'text',
  isPending,
  onSave,
  error,
  testId = 'custom-fields-value-text-trigger',
}: InlineTextEditorProps) {
  const { t } = useTranslation('customFields');
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function commit() {
    const next = inputRef.current?.value ?? '';
    const accepted = onSave(next);
    if (accepted !== false) setEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') setEditing(false);
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-0.5">
        <Input
          ref={inputRef}
          type={type}
          defaultValue={value ?? ''}
          autoFocus
          disabled={isPending}
          className="h-6 w-full border-border bg-background px-1 py-0 text-sm shadow-none"
          aria-label={t('fieldValue.saveAria')}
          onBlur={commit}
          onKeyDown={handleKeyDown}
        />
        {error && (
          <span
            className="text-xs text-destructive"
            data-testid="custom-fields-value-validation-message"
          >
            {t(error)}
          </span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="truncate text-sm text-foreground hover:underline"
      aria-label={t('fieldValue.editAria')}
      onClick={() => setEditing(true)}
      data-testid={testId}
    >
      {value || <span className="text-muted-foreground">{t('fieldValue.empty')}</span>}
    </button>
  );
}

interface CheckboxEditorProps {
  value: string | null;
  isPending: boolean;
  onSave: SaveFn;
}

export function CheckboxEditor({ value, isPending, onSave }: CheckboxEditorProps) {
  return (
    <Switch
      checked={value === 'true'}
      disabled={isPending}
      onCheckedChange={(checked) => onSave(checked ? 'true' : 'false')}
      data-testid="custom-fields-value-checkbox"
    />
  );
}

interface DateEditorProps {
  value: string | null;
  isPending: boolean;
  onSave: SaveFn;
}

export function DateEditor({ value, isPending, onSave }: DateEditorProps) {
  const { t } = useTranslation('customFields');
  const { dateFormat } = useDateFormat();
  const [open, setOpen] = useState(false);

  // Stored value is a floating `yyyy-MM-dd` (opaque to the backend); display it in the
  // user's date format and emit `yyyy-MM-dd` via `format` (no toISOString day-shift).
  const display = value ? format(parseDatePickerValue(value), dateFormat) : null;

  function handleSelect(date: Date | undefined) {
    if (!date) return;
    onSave(format(date, 'yyyy-MM-dd'));
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={(o) => !isPending && setOpen(o)}>
      <PopoverTrigger asChild disabled={isPending}>
        <button
          type="button"
          className="truncate text-sm text-foreground hover:underline"
          aria-label={t('fieldValue.editAria')}
          data-testid="custom-fields-value-date-trigger"
        >
          {display ?? <span className="text-muted-foreground">{t('fieldValue.empty')}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value ? parseDatePickerValue(value) : undefined}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  );
}

interface SelectEditorProps {
  value: string | null;
  options: ProjectFieldOption[];
  isPending: boolean;
  onSave: SaveFn;
}

export function SelectEditor({ value, options, isPending, onSave }: SelectEditorProps) {
  const { t } = useTranslation('customFields');
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  if (options.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('fieldValue.noOptions')}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-sm hover:underline"
          aria-label={t('fieldValue.editAria')}
          data-testid="custom-fields-value-select-trigger"
        >
          {selected ? (
            <>
              {selected.color && (
                <span
                  className="size-2 rounded-full shrink-0"
                  style={{ background: resolveColorValue(selected.color) }}
                />
              )}
              <span className="truncate">{selected.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{t('fieldValue.selectPlaceholder')}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-44 p-1"
        data-testid="custom-fields-value-select-popover"
      >
        {options.map((opt) => (
          <button
            type="button"
            key={opt.id ?? opt.value}
            disabled={isPending}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent',
              opt.value === value && 'bg-accent',
            )}
            onClick={() => {
              onSave(opt.value ?? '');
              setOpen(false);
            }}
          >
            {opt.color && (
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: resolveColorValue(opt.color) }}
              />
            )}
            {opt.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

interface MultiSelectEditorProps {
  value: string | null;
  options: ProjectFieldOption[];
  isPending: boolean;
  onSave: SaveFn;
}

/** Parses the JSON array the backend expects for `MultiSelect`, e.g. `'["red","blue"]'`. */
function parseMultiSelectValue(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function MultiSelectEditor({ value, options, isPending, onSave }: MultiSelectEditorProps) {
  const { t } = useTranslation('customFields');
  const [open, setOpen] = useState(false);
  const selected = parseMultiSelectValue(value);

  function toggleOption(optValue: string) {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onSave(JSON.stringify(next));
  }

  if (options.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('fieldValue.noOptions')}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-wrap items-center gap-1 text-sm hover:opacity-80"
          aria-label={t('fieldValue.editAria')}
          data-testid="custom-fields-value-multiselect-trigger"
        >
          {selected.length > 0 ? (
            selected.map((v) => {
              const opt = options.find((o) => o.value === v);
              return (
                <Badge key={v} variant="secondary" className="h-4 px-1 text-xs">
                  {opt?.name ?? v}
                </Badge>
              );
            })
          ) : (
            <span className="text-muted-foreground">{t('fieldValue.selectPlaceholder')}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-44 p-1"
        data-testid="custom-fields-value-multiselect-popover"
      >
        {options.map((opt) => (
          <div
            key={opt.id ?? opt.value}
            className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
          >
            <Checkbox
              id={`ms-${opt.id ?? opt.value}`}
              checked={selected.includes(opt.value ?? '')}
              disabled={isPending}
              onCheckedChange={() => toggleOption(opt.value ?? '')}
            />
            {opt.color && (
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: resolveColorValue(opt.color) }}
              />
            )}
            <label htmlFor={`ms-${opt.id ?? opt.value}`} className="cursor-pointer text-sm">
              {opt.name}
            </label>
          </div>
        ))}
        <div className="border-t pt-1 mt-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-6 text-xs"
            onClick={() => setOpen(false)}
          >
            {t('fieldValue.saveAria')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface TypedTextEditorProps {
  value: string | null;
  isPending: boolean;
  error?: string | null;
  onSave: SaveFn;
}

export function EmailEditor({ value, isPending, error, onSave }: TypedTextEditorProps) {
  return (
    <InlineTextEditor
      value={value}
      type="email"
      isPending={isPending}
      error={error}
      onSave={onSave}
      testId="custom-fields-value-email-trigger"
    />
  );
}

export function UrlEditor({ value, isPending, error, onSave }: TypedTextEditorProps) {
  return (
    <InlineTextEditor
      value={value}
      type="url"
      isPending={isPending}
      error={error}
      onSave={onSave}
      testId="custom-fields-value-url-trigger"
    />
  );
}

export function PhoneEditor({ value, isPending, error, onSave }: TypedTextEditorProps) {
  return (
    <InlineTextEditor
      value={value}
      type="tel"
      isPending={isPending}
      error={error}
      onSave={onSave}
      testId="custom-fields-value-phone-trigger"
    />
  );
}

export function CurrencyEditor({ value, isPending, error, onSave }: TypedTextEditorProps) {
  return (
    <InlineTextEditor
      value={value}
      type="number"
      isPending={isPending}
      error={error}
      onSave={onSave}
      testId="custom-fields-value-currency-trigger"
    />
  );
}
